import { useState, useMemo, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';

export const Products = ({
  groupProducts,
  setCurrentPage,
  setProducts,
  listProducts,
}) => {
  const [user] = useState(
    () => JSON.parse(localStorage.getItem('user')) ?? null,
  );
  const isLogged = Boolean(user);

  const [selectCategory, setSelectCategory] = useState('Todas');
  const [selectBrand, setSelectBrand] = useState('Todas');
  const [hoverCategories, setHoverCategories] = useState(false);
  const [hoverBrands, setHoverBrands] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const HOME_ROUTE = '/';

  useEffect(() => {
    const $ = window?.jQuery || window?.$;
    if ($ && typeof $.fn?.owlCarousel === 'function') {
      const $el = $('.owl-carousel');
      if ($el.data('owl-initialized')) return;
      $el.data('owl-initialized', true);
      $el.owlCarousel({
        items: 1,
        loop: true,
        autoplay: true,
        autoplayTimeout: 5000,
        autoplayHoverPause: true,
        dots: true,
        nav: false,
        smartSpeed: 500,
      });
    }
  }, []);

  useEffect(() => {
    if (!isLogged) {
      const segments = decodeURIComponent(location.pathname || '')
        .split('/')
        .filter(Boolean);
      const firstSeg = (segments[0] || '').trim().toLowerCase();
      if (firstSeg === 'smartphone') {
        navigate(HOME_ROUTE, { replace: true });
      }
    }
  }, [isLogged, location.pathname, navigate]);

  // helpers
  const uniq = (arr) => Array.from(new Set(arr));
  const norm = (s) => (s || '').trim();
  const isSmartphone = (s) => norm(s).toLowerCase() === 'smartphone';

  const allBrandsRaw = useMemo(
    () => uniq(listProducts.map((p) => p?.Marca?.Marca).filter(Boolean)).sort(),
    [listProducts],
  );
  const allCategoriesRaw = useMemo(
    () =>
      uniq(listProducts.map((p) => norm(p?.Tipo?.Nombre)).filter(Boolean)).sort(
        (a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }),
      ),
    [listProducts],
  );

  const allCategories = useMemo(
    () =>
      isLogged
        ? allCategoriesRaw
        : allCategoriesRaw.filter((c) => !isSmartphone(c)),
    [allCategoriesRaw, isLogged],
  );

  const brandsForSelectedCategory = useMemo(() => {
    const effectiveCategory =
      !isLogged && isSmartphone(selectCategory) ? 'Todas' : selectCategory;
    const source =
      effectiveCategory === 'Todas'
        ? listProducts
        : listProducts.filter(
            (p) => norm(p?.Tipo?.Nombre) === effectiveCategory,
          );
    return uniq(source.map((p) => p?.Marca?.Marca).filter(Boolean)).sort();
  }, [listProducts, selectCategory, isLogged]);

  const categoriesForSelectedBrandRaw = useMemo(() => {
    const source =
      selectBrand === 'Todas'
        ? listProducts
        : listProducts.filter((p) => p?.Marca?.Marca === selectBrand);
    return uniq(source.map((p) => norm(p?.Tipo?.Nombre)).filter(Boolean)).sort(
      (a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }),
    ); // <-- ordenar aquí también
  }, [listProducts, selectBrand]);

  const categoriesForSelectedBrand = useMemo(
    () =>
      isLogged
        ? categoriesForSelectedBrandRaw
        : categoriesForSelectedBrandRaw.filter((c) => !isSmartphone(c)),
    [categoriesForSelectedBrandRaw, isLogged],
  );

  const categoriesToShow = useMemo(() => {
    const base =
      selectBrand === 'Todas'
        ? groupProducts?.length
          ? groupProducts
              .slice()
              .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })) // <-- ordenar aquí si groupProducts existe
          : allCategories
        : categoriesForSelectedBrand
            .slice()
            .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })); // <-- ordenar aquí
    const visible = isLogged ? base : base.filter((c) => !isSmartphone(c));
    return ['Todas', ...visible.filter((c) => c !== 'Todas')];
  }, [
    selectBrand,
    categoriesForSelectedBrand,
    groupProducts,
    allCategories,
    isLogged,
  ]);

  const brandsToShow = useMemo(() => {
    const base =
      selectCategory === 'Todas' ? allBrandsRaw : brandsForSelectedCategory;
    return ['Todas', ...base.filter((b) => b !== 'Todas')];
  }, [selectCategory, brandsForSelectedCategory, allBrandsRaw]);

  const resetCurrentPage = () => setCurrentPage(1);

  const applyFilters = (category, brand) => {
    resetCurrentPage();
    const effectiveCategory =
      !isLogged && isSmartphone(category) ? 'Todas' : category;

    const filtered = listProducts.filter((p) => {
      const cat = norm(p?.Tipo?.Nombre);
      const brd = p?.Marca?.Marca;
      if (!isLogged && isSmartphone(cat)) return false;
      const okCat = effectiveCategory === 'Todas' || cat === effectiveCategory;
      const okBrd = brand === 'Todas' || brd === brand;
      return okCat && okBrd;
    });

    setProducts(filtered);
  };

  useEffect(() => {
    if (!isLogged && isSmartphone(selectCategory)) {
      setSelectCategory('Todas');
      applyFilters('Todas', selectBrand);
    }
  }, [isLogged, selectCategory, selectBrand]);

  const modifySelectedCategory = (category) => {
    setHoverCategories(false); // cierra al seleccionar
    if (!isLogged && isSmartphone(category)) {
      setSelectCategory('Todas');
      applyFilters('Todas', selectBrand);
      return;
    }

    const nextBrands =
      category === 'Todas'
        ? allBrandsRaw
        : uniq(
            listProducts
              .filter((p) => norm(p?.Tipo?.Nombre) === category)
              .map((p) => p?.Marca?.Marca)
              .filter(Boolean),
          );

    const brandStillValid = nextBrands.includes(selectBrand);
    const nextBrand = brandStillValid ? selectBrand : 'Todas';

    setSelectCategory(category);
    if (!brandStillValid) setSelectBrand('Todas');
    applyFilters(category, nextBrand);
  };

  const modifySelectedBrand = (brand) => {
    setHoverBrands(false); // cierra al seleccionar

    const nextCategoriesRaw =
      brand === 'Todas'
        ? allCategoriesRaw
        : uniq(
            listProducts
              .filter((p) => p?.Marca?.Marca === brand)
              .map((p) => norm(p?.Tipo?.Nombre))
              .filter(Boolean),
          );

    const nextCategories = isLogged
      ? nextCategoriesRaw
      : nextCategoriesRaw.filter((c) => !isSmartphone(c));

    const categoryStillValid = nextCategories.includes(selectCategory);
    const nextCategory = categoryStillValid ? selectCategory : 'Todas';

    setSelectBrand(brand);
    if (!categoryStillValid) setSelectCategory('Todas');
    applyFilters(nextCategory, brand);
  };

  const linkActive = (isActive, type = '', filter = '') => {
    if (isActive) {
      if (type === 'category') setSelectCategory(filter);
      if (type === 'brand') setSelectBrand(filter);
      return 'btn btn-category active';
    }
    return 'btn btn-category';
  };

  const searchProducts = (e) => {
    setCurrentPage(1);
    const q = e.target.value.toLowerCase();

    if (q) {
      const filtered = listProducts.filter(
        (p) =>
          (p?.Referencia || '').toLowerCase().includes(q) ||
          (p?.Caracteristicas || '').toLowerCase().includes(q) ||
          (p?.Categoria?.Categoria || '').toLowerCase().includes(q),
      );
      setProducts(
        isLogged
          ? filtered
          : filtered.filter((p) => !isSmartphone(p?.Tipo?.Nombre)),
      );
    } else {
      applyFilters(selectCategory, selectBrand);
    }
  };

  // ========= FIX: handlers para limpiar y cerrar sin reabrir =========
  const handleClearCategory = (e) => {
    e.stopPropagation(); // evita que el contenedor toggle se dispare
    setHoverCategories(false); // cierra el dropdown
    const brandStillValid = allBrandsRaw.includes(selectBrand);
    const nextBrand = brandStillValid ? selectBrand : 'Todas';
    if (!brandStillValid) setSelectBrand('Todas');
    setSelectCategory('Todas');
    applyFilters('Todas', nextBrand);
    navigate(`${nextBrand === 'Todas' ? '' : '/'.concat(nextBrand)}`);
  };

  const handleClearBrand = (e) => {
    e.stopPropagation();
    setHoverBrands(false);
    const categoryStillValid = allCategories.includes(selectCategory);
    const nextCategory = categoryStillValid ? selectCategory : 'Todas';
    if (!categoryStillValid) setSelectCategory('Todas');
    setSelectBrand('Todas');
    applyFilters(nextCategory, 'Todas');
    navigate(`${nextCategory === 'Todas' ? '' : nextCategory}`);
  };
  // ===================================================================

  return (
    <>
      {/* -------- Banner -------- */}
      <style>{`
        .banner__img img { width: 100%; height: 100%; object-fit: cover; }
        .banner__img { max-height: 400px; overflow: hidden; border-radius: 6px; }
        @media (max-width: 768px) { .banner__img { max-height: 260px; } }
      `}</style>

      <section className="banner section w-full pt-10 flex items-center justify-center">
        <div className="w-full">
          <div className="p-10 owl-carousel owl-theme w-full">
            <div className="item w-full">
              <div className="banner__img w-full">
                <img
                  src="./img/Banner1-tecnosuper.png"
                  alt="Banner 1"
                  loading="eager"
                />
              </div>
            </div>
            <div className="item w-full">
              <div className="banner__img w-full">
                <img
                  src="./img/Banner2-tecnosuper.png"
                  alt="Banner 2"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="item w-full">
              <div className="banner__img w-full">
                <img
                  src="./img/Banner-Hyundai.webp"
                  alt="Banner 2"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------- Productos -------- */}
      <section className="products section">
        <div className="container fluid">
          <div className="row">
            <div className="col-100 col-100 col-mb-100">
              <div className="header-mb w-full flex flex-col items-center">
                <nav className="header__nav flex items-center justify-center w-full px-4 md:px-0">
                  <button className="btn btn-search p-2 rounded-full bg-gray-200">
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </button>
                  <input
                    type="search"
                    placeholder="Buscar por marca o referencia"
                    id="search-product"
                    onChange={searchProducts}
                    className="bg-gray-100 text-black px-4 py-2 rounded-full w-full md:w-96 shadow-md outline-none"
                  />
                </nav>
              </div>
            </div>
          </div>

          {user && (
            <section className="user-info-card">
              <div className="user-info">
                <span className="icon">
                  <i className="fa-solid fa-circle-user"></i>
                </span>
                <div className="user-details">
                  <span className="bold">
                    {user.usuario.charAt(0).toUpperCase() +
                      user.usuario.slice(1).toLowerCase()}
                  </span>
                  <span className="user-profile">{user.userType}</span>
                </div>
              </div>
              <div className="user-info">
                <span className="icon">
                  <i className="fa-solid fa-wallet"></i>
                </span>
                <span className="cupo-text">Cupo disponible:</span>
                <span className="bold green">
                  {parseInt(user.availableAmount).toLocaleString('es-CO', {
                    style: 'currency',
                    currency: 'COP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            </section>
          )}

          <div className="row">
            {/* CATEGORÍAS */}
            <div className="col col-50 col-mb-100">
              <div className="products__category py-3">
                <h3 className="title">Categorías</h3>

                <div
                  className="products__cont-categories"
                  // FIX: quitamos el onClick del contenedor para evitar toggles por burbuja
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    flexWrap: 'wrap',
                    gap: '8px',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  <div
                    className="products__category_active"
                    // FIX: toggle SOLO aquí
                    onClick={() => setHoverCategories((v) => !v)}
                    aria-expanded={hoverCategories}
                    style={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      color: '#7c3aed',
                      fontWeight: 'bold',
                      borderRadius: '2px',
                      marginRight: '0.5rem',
                      fontSize: '1rem',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {selectCategory}
                    </span>
                    <i className="fa-solid fa-chevron-down"></i>
                  </div>

                  {selectCategory !== 'Todas' && (
                    <button
                      style={{
                        marginLeft: '0',
                        marginTop: '0',
                        background: '#7c3aed',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(124,58,237,0.15)',
                        transition: 'background 0.2s, transform 0.2s',
                        fontSize: '16px',
                        flexShrink: 0,
                      }}
                      title="Quitar filtro de categoría"
                      onClick={handleClearCategory} // FIX: limpiamos y cerramos sin reabrir
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#a78bfa';
                        e.currentTarget.style.transform = 'scale(1.08)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#7c3aed';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                  )}

                  <div
                    className={
                      hoverCategories
                        ? 'products__cont_list_categories active'
                        : 'products__cont_list_categories'
                    }
                    // FIX: evitar burbuja dentro del dropdown
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      width: '99.3%',
                      minWidth: '180px',
                    }}
                  >
                    <ul>
                      {categoriesToShow.map((cat, i) => (
                        <li key={i}>
                          <NavLink
                            to={`${cat === 'Todas' ? '' : cat}${
                              selectBrand === 'Todas' ||
                              selectBrand === undefined
                                ? ''
                                : '/'.concat(selectBrand)
                            }`}
                            className={({ isActive }) =>
                              linkActive(isActive, 'category', cat)
                            }
                            onClick={() => modifySelectedCategory(cat)}
                          >
                            {cat}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* MARCAS */}
            <div className="col col-50 col-mb-100">
              <div className="products__category py-3">
                <h3 className="title">Marcas</h3>

                <div
                  className="products__cont-categories"
                  // FIX: sin onClick aquí
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    flexWrap: 'wrap',
                    gap: '8px',
                    width: '94%',
                    maxWidth: '100%',
                  }}
                >
                  <div
                    className="products__category_active"
                    // FIX: toggle SOLO aquí
                    onClick={() => setHoverBrands((v) => !v)}
                    aria-expanded={hoverBrands}
                    style={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      color: '#7c3aed',
                      fontWeight: 'bold',
                      borderRadius: '2px',
                      marginRight: '0.5rem',
                      fontSize: '1rem',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <span style={{ textOverflow: 'ellipsis' }}>
                      {selectBrand}
                    </span>
                    <i className="fa-solid fa-chevron-down"></i>
                  </div>

                  {selectBrand !== 'Todas' && (
                    <button
                      style={{
                        marginLeft: '0',
                        marginTop: '0',
                        background: '#7c3aed',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(124,58,237,0.15)',
                        transition: 'background 0.2s, transform 0.2s',
                        fontSize: '16px',
                        flexShrink: 0,
                      }}
                      title="Quitar filtro de marca"
                      onClick={handleClearBrand} // FIX: limpiamos y cerramos sin reabrir
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#a78bfa';
                        e.currentTarget.style.transform = 'scale(1.08)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#7c3aed';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                  )}

                  <div
                    className={
                      hoverBrands
                        ? 'products__cont_list_categories active'
                        : 'products__cont_list_categories'
                    }
                    // FIX: evitar burbuja dentro del dropdown
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      width: '99%',
                    }}
                  >
                    <ul>
                      {brandsToShow.map((brand, i) => (
                        <li key={i}>
                          <NavLink
                            to={`${
                              selectCategory === 'Todas' ||
                              selectCategory === undefined
                                ? ''
                                : selectCategory
                            }/${brand === 'Todas' ? '' : brand}`}
                            className={({ isActive }) =>
                              linkActive(isActive, 'brand', brand)
                            }
                            onClick={() => modifySelectedBrand(brand)}
                          >
                            {brand}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="products__cont-categories"></div>
              </div>
            </div>

            {/* GRID DE PRODUCTOS */}
            <div className="col col-100 col-mb-100" style={{ margin: ' ' }}>
              <div className="row">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
